import { FormEvent, useMemo, useState } from 'react';
import { addGuest } from '../api';
import { calculateGuestStats } from '../lib/guestStats';
import { Guest, NewGuest } from '../types';

type GuestSectionProps = {
  guests: Guest[];
  onGuestAdded: (guest: Guest) => void;
  onUnauthorized: () => void;
};

const initialGuest: NewGuest = {
  name: '',
  weisswurst: 0,
  bier: false,
  aperol: false,
  alkoholfrei: false,
  moscowMule: false,
  getraenke: '',
};

export default function GuestSection({ guests, onGuestAdded, onUnauthorized }: GuestSectionProps) {
  const [form, setForm] = useState<NewGuest>(initialGuest);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const stats = useMemo(() => calculateGuestStats(guests), [guests]);

  const set = <K extends keyof NewGuest>(key: K, value: NewGuest[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) {
      document.getElementById('f-name')?.focus();
      return;
    }
    
    setSubmitting(true);
    setError('');
    try {
      const guest = await addGuest({ ...form, name, getraenke: form.getraenke.trim() });
      onGuestAdded(guest);
      setForm(initialGuest);
      requestAnimationFrame(() => document.getElementById('f-name')?.focus());
    } 
    catch (requestError) {
      if (requestError instanceof Error && requestError.name === 'UnauthorizedError') {
        onUnauthorized();
      } 
      else {
        setError(requestError instanceof Error ? requestError.message : 'Eintragen fehlgeschlagen.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const statItems = [
    [String(stats.guests), 'Gäste'],
    [String(stats.weisswurst), 'Weißwürste gesamt'],
    [`${stats.beerLitres} L`, 'Bier'],
    [String(stats.aperol), 'Aperol'],
    [String(stats.alkoholfrei), 'Alkoholfrei'],
    [String(stats.moscowMule), 'Moscow Mule'],
  ];

  return (
    <section className="decorated-section">
      <span className="watermark watermark-servus" aria-hidden="true">Servus!</span>
      <SectionHeader number="04">Wer ist dabei?</SectionHeader>
      <div className="guest-panel">
        <form onSubmit={submit}>
          <div className="form-grid">
            <div>
              <label className="field-label" htmlFor="f-name">Name</label>
              <input id="f-name" type="text" placeholder="Dein Name" maxLength={80} value={form.name} onChange={(event) => set('name', event.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="f-weisswurst">Weißwürste</label>
              <input id="f-weisswurst" type="number" min="0" max="50" value={form.weisswurst} onChange={(event) => set('weisswurst', Number(event.target.value) || 0)} />
            </div>
          </div>
          <div className="form-grid">
            <div>
              <span className="field-label">Trinkt Bier?</span>
              <label className="check-field" htmlFor="f-bier">
                <input id="f-bier" type="checkbox" checked={form.bier} onChange={(event) => set('bier', event.target.checked)} />
                <span>Ja</span>
              </label>
            </div>
            <div>
              <label className="field-label" htmlFor="f-getraenke">Sonstiges (frei)</label>
              <input id="f-getraenke" type="text" maxLength={160} placeholder="z. B. Radler, Wein …" value={form.getraenke} onChange={(event) => set('getraenke', event.target.value)} />
            </div>
          </div>
          <div className="wish-fields">
            <span className="field-label">Weitere Getränkewünsche</span>
            <div className="check-grid">
              <CheckField id="f-aperol" label="Aperol" checked={form.aperol} onChange={(value) => set('aperol', value)} />
              <CheckField id="f-alkoholfrei" label="Alkoholfrei" checked={form.alkoholfrei} onChange={(value) => set('alkoholfrei', value)} />
              <CheckField id="f-moscowmule" label="Moscow Mule" checked={form.moscowMule} onChange={(value) => set('moscowMule', value)} />
            </div>
          </div>
          <button className="primary-button" type="submit" disabled={submitting}>Eintragen</button>
          <div className="form-error" role="alert">{error}</div>
        </form>

        {guests.length === 0 ? (
          <div className="empty-guests">Noch keine Anmeldungen — sei die/der Erste!</div>
        ) : (
          <details className="guest-list">
            <summary>Gästeliste anzeigen ({guests.length})</summary>
            <ul>
              {guests.map((guest) => <li key={guest.id}>{guest.name}</li>)}
            </ul>
          </details>
        )}

        <div className="stats-grid">
          {statItems.map(([value, label]) => (
            <div className="stat-card" key={label}>
              <div className="stat-value">{value}</div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>
        <div className="shared-note">Diese Liste ist für alle sichtbar, die diese Seite öffnen.</div>
      </div>
    </section>
  );
}

function CheckField({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="check-field" htmlFor={id}>
      <input id={id} type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function SectionHeader({ number, children }: { number: string; children: React.ReactNode }) {
  return (
    <div className="section-header">
      <span>{number}</span>
      <h2>{children}</h2>
    </div>
  );
}
